import { ExternalLink, FileCheck, Eye, Bell } from 'lucide-react';

export function SubPortalSection() {
  return (
    <section id="sub-portal" className="relative py-28 sm:py-32 overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, rgba(17,17,17,0) 0%, rgba(26,26,26,0.4) 50%, rgba(17,17,17,0) 100%)',
        }}
      />

      <div className="relative mx-auto max-w-6xl px-6">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          {/* Left - description */}
          <div className="reveal">
            <span className="mb-4 inline-block rounded-full border border-build-700/40 bg-build-900/40 px-3 py-1 text-xs font-medium text-orange-400">
              Subcontractor Portal
            </span>
            <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
              Your Subs Get Their Own Portal
            </h2>
            <p className="mb-8 text-zinc-400 leading-relaxed">
              No app downloads. No accounts to create. Send your subcontractors a link and they can view job details,
              sign lien waivers, upload documents, and see payment status — all from their phone or computer.
            </p>

            <div className="space-y-4">
              {[
                {
                  icon: ExternalLink,
                  title: 'Token-Based Access',
                  desc: 'Each sub gets a unique, secure link. No passwords to remember.',
                },
                {
                  icon: FileCheck,
                  title: 'Sign Lien Waivers',
                  desc: 'Subs can review and sign waivers directly in the portal.',
                },
                {
                  icon: Eye,
                  title: 'View Job Details',
                  desc: 'See scope of work, schedules, and uploaded documents for their assigned jobs.',
                },
                {
                  icon: Bell,
                  title: 'Payment Visibility',
                  desc: 'Subs can see payment status so they stop calling you asking "where is my check?"',
                },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-500/10">
                    <item.icon className="h-4 w-4 text-orange-400" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-zinc-200">{item.title}</h4>
                    <p className="text-sm text-zinc-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - mockup */}
          <div className="reveal">
            <div className="mockup-window">
              <div className="mockup-titlebar">
                <div className="mockup-dot" style={{ background: '#ef4444' }} />
                <div className="mockup-dot" style={{ background: '#f59e0b' }} />
                <div className="mockup-dot" style={{ background: '#22c55e' }} />
                <span className="ml-2 text-xs text-zinc-500">Sub Portal - Martinez Electrical</span>
              </div>
              <div className="p-5 space-y-4">
                {/* Portal header */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-zinc-200">Martinez Electrical LLC</p>
                    <p className="text-[10px] text-zinc-500">Assigned to: Johnson Residence - Kitchen Remodel</p>
                  </div>
                  <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-[10px] font-medium text-green-400">Active</span>
                </div>

                {/* Tasks */}
                <div className="rounded-lg border border-build-700/20 bg-build-800/30">
                  <div className="border-b border-build-700/15 px-3 py-2">
                    <span className="text-[10px] font-medium text-orange-400">Pending Actions</span>
                  </div>
                  <div className="divide-y divide-build-700/10">
                    <div className="flex items-center justify-between px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-amber-500" />
                        <span className="text-xs text-zinc-300">Sign Conditional Lien Waiver</span>
                      </div>
                      <button className="rounded bg-orange-500/20 px-2 py-0.5 text-[10px] font-medium text-orange-300">Sign Now</button>
                    </div>
                    <div className="flex items-center justify-between px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-blue-500" />
                        <span className="text-xs text-zinc-300">Upload Insurance Certificate</span>
                      </div>
                      <button className="rounded bg-blue-500/20 px-2 py-0.5 text-[10px] font-medium text-blue-300">Upload</button>
                    </div>
                  </div>
                </div>

                {/* Payment status */}
                <div className="rounded-lg border border-build-700/20 bg-build-800/30 px-3 py-3">
                  <span className="text-[10px] font-medium text-zinc-500">Payment Status</span>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-zinc-300">Draw #3 - Rough-In Complete</span>
                    <span className="text-xs font-medium text-green-400">$4,200 Paid</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-xs text-zinc-300">Draw #4 - Trim Out</span>
                    <span className="text-xs font-medium text-amber-400">Pending</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
